import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setConnected,
  setMicNotFound,
  setMicPermissionDenied,
  setRecording,
} from "@store/microphoneSlice";
import sleep from "@utils/sleep";
import { setMessage } from "@store/snackbarSlice";
import {
  VoiceDetectionEvent,
  VoiceDetector,
} from "@service/voice-detection/voice-detector";
import { VoiceDetectorFactory } from "@service/voice-detection/voice-detector.factory";
import { RootState } from "@store/store";
import { useTranslation } from "react-i18next";
import { MicStream } from "@service/mic-stream";

const MIC_CHECK_INTERVAL = 1000;

export const useRecordVoice = (
  processing: boolean,
  onVoiceRecorded: (blob: Blob | ArrayBufferLike) => void,
  streaming: boolean,
) => {
  const mediaRecorder = useRef<MediaRecorder | null>(null);
  const micStream = useRef<MicStream | null>(null);
  const mediaStream = useRef<MediaStream | null>(null);
  const voiceDetector = useRef<VoiceDetector | null>(null);
  const chunks = useRef<Blob[]>([]);
  const initialized = useRef<boolean>(false);
  const dispatch = useDispatch();
  const muted = useSelector((state: RootState) => state.avatar.muted);
  const processingRef = useRef<boolean>(false);
  const { t } = useTranslation();

  useEffect(() => {
    const audio = mediaStream.current?.getAudioTracks()?.[0];
    if (audio) {
      audio.enabled = !muted;
    }
  }, [muted]);

  const startRecording = useCallback((): void => {
    if (micStream.current) {
      micStream.current.start();
      dispatch(setRecording(true));
    }
    if (mediaRecorder.current && mediaRecorder.current.state !== "recording") {
      mediaRecorder.current.start();
      dispatch(setRecording(true));
    }
  }, [dispatch]);

  const stopRecording = useCallback((): void => {
    if (micStream.current) {
      micStream.current.stop();
      dispatch(setRecording(false));
    }
    if (mediaRecorder.current) {
      mediaRecorder.current.stop();
      dispatch(setRecording(false));
    }
  }, [dispatch]);

  useEffect(() => {
    processingRef.current = processing;
    if (processing) {
      stopRecording();
    }
  }, [processing, stopRecording]);

  const initMediaRecorder = (stream: MediaStream): MediaRecorder => {
    const mediaRecorder = new MediaRecorder(stream);

    mediaRecorder.ondataavailable = (e) => {
      chunks.current.push(e.data);
    };

    mediaRecorder.onstop = async () => {
      const audioBlob = new Blob(chunks.current, { type: "audio/wav" });
      onVoiceRecorded(audioBlob);
      chunks.current = [];
    };

    return mediaRecorder;
  };

  const getAudioStream = async (): Promise<MediaStream | null> => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });
      dispatch(setMicNotFound(false));
      dispatch(setMicPermissionDenied(false));
      return stream;
    } catch (e) {
      console.error(e);
      switch ((e as any)?.name) {
        case "NotFoundError": {
          dispatch(setMicNotFound(true));
          break;
        }
        case "NotAllowedError": {
          dispatch(setMicPermissionDenied(true));
          break;
        }
        default: {
          dispatch(
            setMessage({ content: t("errors.unknown"), severity: "error" }),
          );
          return null;
        }
      }
      await sleep(MIC_CHECK_INTERVAL);
      return getAudioStream();
    }
  };

  const handleVoiceDetectionEvent = ({ type }: VoiceDetectionEvent) => {
    if (type === "start") {
      if (!processingRef.current) {
        startRecording();
      }
    } else {
      stopRecording();
    }
  };

  const init = async () => {
    if (initialized.current) {
      return;
    }
    initialized.current = true;
    const stream = await getAudioStream();
    if (!stream) {
      return;
    }
    mediaStream.current = stream;

    if (streaming) {
      micStream.current = new MicStream(stream, onVoiceRecorded);
    } else {
      mediaRecorder.current = await initMediaRecorder(stream);
    }

    voiceDetector.current = VoiceDetectorFactory.create({
      stream,
      callback: handleVoiceDetectionEvent,
    });
    dispatch(setConnected(true));
  };

  useEffect(() => {
    init();

    return () => {
      if (mediaStream.current) {
        mediaStream.current.getAudioTracks().forEach(function (track) {
          track.stop();
        });
      }
      if (voiceDetector.current) {
        voiceDetector.current.destroy();
      }
      if (micStream.current) {
        micStream.current.destroy();
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return { startRecording, stopRecording };
};
